import { useCallback, useState } from "react";
import { format } from "date-fns";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { Box, Button, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../components/CustomTable";
import { StyledTableCell } from "../../components/styled";
import {
  activateDailyPoll,
  deactivateDailyPoll,
  getDailyPolls,
} from "../../services/dailyPoll.service";
import { DATE_TIME_FORMAT } from "../../utils/constants";

const DailyPollList = () => {
  const queryClient = useQueryClient();
  const [pendingActionId, setPendingActionId] = useState(null);

  const { mutate: activatePoll, isPending: isActivating } = useMutation({
    mutationKey: ["activateDailyPoll"],
    mutationFn: (pollId) => activateDailyPoll(pollId),
    onSuccess: () => {
      alert("Daily poll activated");
      queryClient.invalidateQueries(["daily-polls"]);
    },
    onError: (error) => {
      alert(`Error activating daily poll: ${error || "Unknown error"}`);
    },
    onSettled: () => {
      setPendingActionId(null);
    },
  });

  const { mutate: deactivatePoll, isPending: isDeactivating } = useMutation({
    mutationKey: ["deactivateDailyPoll"],
    mutationFn: (pollId) => deactivateDailyPoll(pollId),
    onSuccess: () => {
      alert("Daily poll deactivated");
      queryClient.invalidateQueries(["daily-polls"]);
    },
    onError: (error) => {
      alert(`Error deactivating daily poll: ${error || "Unknown error"}`);
    },
    onSettled: () => {
      setPendingActionId(null);
    },
  });

  const hasOtherActivePoll = useCallback(
    (pollId) => {
      const cached = queryClient.getQueriesData({
        queryKey: ["daily-polls"],
      });
      return cached.some(([, data]) =>
        (data?.items ?? []).some(
          (item) => item?.isActive && String(item.id) !== String(pollId),
        ),
      );
    },
    [queryClient],
  );

  const handleActivate = (poll) => {
    if (!poll?.id || isActivating || isDeactivating) {
      return;
    }

    if (hasOtherActivePoll(poll.id)) {
      const shouldContinue = window.confirm(
        "Another poll is currently active. Activating this poll may deactivate it. Continue?",
      );
      if (!shouldContinue) {
        return;
      }
    }

    setPendingActionId(poll.id);
    activatePoll(poll.id);
  };

  const handleDeactivate = (poll) => {
    if (!poll?.id || isActivating || isDeactivating) {
      return;
    }

    setPendingActionId(poll.id);
    deactivatePoll(poll.id);
  };

  const renderHeader = () => (
    <TableRow>
      <StyledTableCell>Question</StyledTableCell>
      <StyledTableCell>Publish At</StyledTableCell>
      <StyledTableCell>Expires At</StyledTableCell>
      <StyledTableCell>Active</StyledTableCell>
      <StyledTableCell>Total Votes</StyledTableCell>
      <StyledTableCell>Action</StyledTableCell>
    </TableRow>
  );

  const renderRow = (poll) => {
    const isRowPending =
      pendingActionId === poll.id && (isActivating || isDeactivating);
    return (
      <TableRow key={poll.id}>
        <TableCell>{poll.question || "N/A"}</TableCell>

        <TableCell>
          {poll.publishAt
            ? format(new Date(poll.publishAt), DATE_TIME_FORMAT)
            : "N/A"}
        </TableCell>

        <TableCell>
          {poll.expiresAt
            ? format(new Date(poll.expiresAt), DATE_TIME_FORMAT)
            : "N/A"}
        </TableCell>

        <TableCell>{poll.isActive ? "Yes" : "No"}</TableCell>

        <TableCell>{poll.totalVotes ?? 0}</TableCell>

        <TableCell>
          <IconButton
            aria-label="view"
            component={Link}
            to={`${poll.id}`}
            state={{ poll }}
          >
            <VisibilityIcon />
          </IconButton>

          <IconButton
            aria-label="activate"
            onClick={() => handleActivate(poll)}
            disabled={poll.isActive || isRowPending}
          >
            <PlayArrowIcon />
          </IconButton>

          <IconButton
            aria-label="deactivate"
            onClick={() => handleDeactivate(poll)}
            disabled={!poll.isActive || isRowPending}
          >
            <StopIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/daily-polls/create"
        >
          Create Daily Poll
        </Button>
      </Box>

      <CustomTable
        name={"daily-polls"}
        getItemsMethod={getDailyPolls}
        renderHeader={renderHeader}
        renderRow={renderRow}
        columnCount={6}
      />
    </>
  );
};

export default DailyPollList;
